/**
 * Rebuilding a live {@link ComboScore} from the record on disk.
 *
 * `PersistedComboScore` treats every field as optional on purpose — the decoder
 * must never reject a stored favourite, because a missing key is something to
 * migrate, not something to discard. That leniency has to be paid for
 * somewhere, and this is where: every absent field is resolved to the default
 * the spec names, every dangling reference is dropped by the domain's own
 * normalizers, and the result is a score with the invariants Compose, Rehearse
 * and Ringside all assume.
 *
 * Nothing here re-derives a rule. Beat ids come from `normalizeBeatIds`, beats
 * from `hydrateComboBeat`, brackets and branches from `normalizeComboScore`,
 * and the legacy shape from `inferredLegacyShape` via the same call. A second
 * implementation of any of those would be a second thing to get subtly wrong
 * against bytes that are already on disk.
 *
 * The one translation this file *does* own is the stored-id → resolved-id map.
 * A stored bracket or branch references beats by the ids they were written
 * with; when `normalizeBeatIds` has to replace one (it was absent, or it
 * collided with another beat's), a bracket still pointing at the old string
 * would be silently dropped as dangling, and the user's phrase would disappear
 * from a favourite that decoded "successfully".
 */

import type {
  ComboBeat,
  ComboBeatId,
  ComboId,
  ComboPhraseBracket,
  ComboResponseBranch,
  ComboResponseOption,
  ComboScore,
  PersistedComboScore,
} from "@ff/domain";
import {
  DEFAULT_COMBO_PARAMETERS,
  PHRASE_REPEAT_MIN,
  clampPhraseRepeatCount,
  comboBeatId,
  comboScoreRevision,
  deriveBeatId,
  hydrateComboBeat,
  normalizeBeatIds,
  normalizeComboScore,
  phraseBracketId,
  phraseBracketName,
  responseBranchId,
  responseOptionId,
  responseOptionName,
} from "@ff/domain";

/**
 * The revision a favourite is rehydrated at.
 *
 * A revision counts editing transactions applied *in this session*; nothing
 * about a record on disk says how many it took to author. Starting every loaded
 * favourite at zero means "no transaction has been applied to this score since
 * it was opened", which is the only true statement available, and it keeps the
 * counter comparable between a loaded favourite and a freshly generated one.
 */
const LOADED_REVISION = comboScoreRevision(0);

export function hydratePersistedComboScore(
  id: ComboId,
  persisted: PersistedComboScore,
): ComboScore {
  const actions = persisted.actions ?? [];
  const contexts = persisted.beatContexts ?? [];
  const storedIds = persisted.beatIDs ?? [];
  const lockedIds = new Set(persisted.lockedBeatIDs ?? []);

  const proposed = actions.map((_, index) => {
    const stored = storedIds[index];
    return stored === undefined ? undefined : comboBeatId(stored);
  });
  const resolvedIds = normalizeBeatIds(proposed, id, actions.length);

  const beats: ComboBeat[] = actions.map((action, index) => {
    const stored = storedIds[index];
    return hydrateComboBeat(
      resolvedIds[index] ?? deriveBeatId(id, index),
      action,
      contexts[index],
      // A lock is recorded against the id the beat was *written* with. Checking
      // the resolved id instead would silently unlock every beat whose id had
      // to be replaced.
      stored !== undefined && lockedIds.has(stored),
    );
  });

  const resolvedByStoredId = new Map<string, ComboBeatId>();
  storedIds.forEach((stored, index) => {
    const resolved = resolvedIds[index];
    if (stored === undefined || resolved === undefined) return;
    resolvedByStoredId.set(stored, resolved);
  });
  const resolveBeatReference = (raw: string): ComboBeatId =>
    resolvedByStoredId.get(raw) ?? comboBeatId(raw);

  const phraseBrackets: ComboPhraseBracket[] = (persisted.phraseBrackets ?? []).map(
    (bracket) => ({
      id: phraseBracketId(bracket.id),
      name: phraseBracketName(bracket.name ?? ""),
      beatIds: (bracket.beatIDs ?? []).map(resolveBeatReference),
      repeatCount: clampPhraseRepeatCount(bracket.repeatCount ?? PHRASE_REPEAT_MIN),
    }),
  );

  const responseBranches: ComboResponseBranch[] = (persisted.responseBranches ?? []).map(
    (branch) => {
      const options: ComboResponseOption[] = (branch.options ?? []).map((option) => ({
        id: responseOptionId(option.id),
        name: responseOptionName(option.name ?? ""),
        // Branch beats are never locked: a branch is a rehearsal aid, not part
        // of the locked score, so there is no stored lock set to consult.
        beats: (option.beats ?? []).map((beat) =>
          hydrateComboBeat(comboBeatId(beat.id), beat.action, beat.context, false),
        ),
      }));
      const selected = branch.selectedOptionID;
      return {
        id: responseBranchId(branch.id),
        defenseBeatId: resolveBeatReference(branch.defenseBeatID),
        threat: branch.threat,
        options,
        selectedOptionId:
          selected === undefined || selected === null ? null : responseOptionId(selected),
      };
    },
  );

  return normalizeComboScore(
    {
      id,
      beats,
      // A favourite written before these fields existed adopts the app's own
      // defaults rather than a second set invented here (F-CB-14).
      complexity: persisted.complexity ?? DEFAULT_COMBO_PARAMETERS.complexity,
      stance: persisted.stance ?? DEFAULT_COMBO_PARAMETERS.stance,
      createdAt: persisted.createdAt ?? 0,
      // `null` asks `normalizeComboScore` for `inferredLegacyShape`, which reads
      // what the stored beats already do. Substituting the generator defaults
      // instead would show constraints that would remix the favourite into
      // something the user never saved.
      shape: persisted.shape ?? null,
      phraseBrackets,
      responseBranches,
    },
    LOADED_REVISION,
  );
}
